/**
 * Shared display rules for billed / outstanding / retainage amounts.
 * Show $0.00 when a project has submitted/paid pay apps but zero balance;
 * show "—" only when nothing has been billed yet.
 */

import type { Project } from '../api/types'
import { formatCurrency } from './formatters'

export function projectHasBilledPayApps(project: Project): boolean {
  return parseFloat(project.total_billed ?? '0') > 0
}

export function formatOutstandingForProject(
  project: Project,
  draftTotal = 0,
): string {
  const outstanding = parseFloat(project.outstanding ?? '0')
  if (outstanding > 0) return formatCurrency(project.outstanding)
  if (projectHasBilledPayApps(project)) return formatCurrency(0)
  if (draftTotal > 0) return `${formatCurrency(draftTotal)} draft`
  return '—'
}

export function formatRetainageForProject(project: Project): string {
  const held = parseFloat(project.retention_held ?? '0')
  if (!Number.isNaN(held) && held > 0) return formatCurrency(held)
  if (projectHasBilledPayApps(project)) return formatCurrency(0)
  return '—'
}

export function formatOutstandingAmount(
  outstanding: number | string | null | undefined,
  totalBilled: number | string | null | undefined,
): string {
  const out = parseFloat(String(outstanding ?? '0'))
  const billed = parseFloat(String(totalBilled ?? '0'))
  if (out > 0) return formatCurrency(outstanding)
  if (billed > 0) return formatCurrency(0)
  return '—'
}

/**
 * Billing progress — percentage-billing workflow.
 * Billed gross = amount_due total (net of retention) + retention held to date.
 * Contract total = original contract + active change orders on submitted/paid PAs.
 */
export interface BillingProgress {
  contractTotal: number
  billedGross:   number
  remaining:     number   // never negative
  pctBilled:     number   // 0–100+, rounded to 1 decimal
}

export function billingProgressForProject(project: Project): BillingProgress {
  const contract = parseFloat(project.original_contract ?? '0') || 0
  const cos      = parseFloat(project.change_order_total ?? '0') || 0
  const billed   = parseFloat(project.total_billed ?? '0') || 0
  const retained = parseFloat(project.retention_held ?? '0') || 0
  const contractTotal = contract + cos
  const billedGross   = billed + retained
  const remaining     = Math.max(0, contractTotal - billedGross)
  const pctBilled     = contractTotal > 0
    ? Math.round((billedGross / contractTotal) * 1000) / 10
    : 0
  return { contractTotal, billedGross, remaining, pctBilled }
}

export function formatLeftToBillForProject(project: Project): string {
  const { contractTotal, remaining } = billingProgressForProject(project)
  if (contractTotal <= 0) return '—'
  return formatCurrency(remaining)
}
